/**
 * Menyunting nilai kurva dari atas gambarnya.
 *
 * Jendela pemeriksa kurva menyunting angka di dua tempat: tabelnya, tempat
 * angka diketik, dan grafiknya, tempat kurvanya diseret. Keduanya menulis ke
 * satu draft yang sama, dan aritmetika yang menghubungkan sebuah kedudukan
 * kursor dengan sebuah cuplikan kurva ada di sini — bukan di dalam React —
 * sebab yang mudah keliru di sini tidak memerlukan layar untuk salah.
 *
 * Dua kekeliruan yang dijaga modul ini, dan keduanya diam:
 *
 * Yang pertama, nilai dari kedudukan mendatar. Menghitungnya sebagai pecahan
 * lebar dikali rentang benar tepat selama sumbunya linear. Pada resistivitas
 * yang digambar logaritmik — dan itu bawaannya — seretan ke tengah track akan
 * menuliskan setengah rentang alih-alih setengah dekade, meleset sampai satu
 * orde besaran, dan tidak ada satu pun yang terlihat salah sampai seseorang
 * membaca angkanya berbulan-bulan kemudian. Karena itu pemetaan baliknya
 * dipinjam dari `unproject` milik lembar log itu sendiri: yang menggambar dan
 * yang membaca balik harus memakai satu aritmetika, bukan dua yang mirip.
 *
 * Yang kedua, kedalaman. Yang disimpan server adalah nilai pada cuplikan, jadi
 * suntingan harus dialamatkan ke cuplikan yang benar-benar ada — sebuah
 * kedalaman di antara dua cuplikan akan melahirkan cuplikan baru yang tidak
 * pernah diukur siapa pun.
 */
import { project, unproject, VIEW_W } from "../components/LogTracks";

/** Cuplikan terdekat pada sebuah kedalaman, atau `null` bila tidak ada cuplikan. */
export function nearestSample(depths: number[], md: number): number | null {
  if (depths.length === 0 || !Number.isFinite(md)) return null;
  let terdekat = depths[0];
  let jarak = Math.abs(terdekat - md);
  for (const depth of depths) {
    const d = Math.abs(depth - md);
    if (d < jarak) {
      jarak = d;
      terdekat = depth;
    }
  }
  return terdekat;
}

/**
 * Suntingan yang dihasilkan sebuah kedudukan kursor di atas track.
 *
 * `fx` dan `fy` adalah pecahan lebar dan tinggi kotak gambar, 0 di kiri dan di
 * puncak. Di luar kotak memulangkan `null` alih-alih menjepit ke tepi: sebuah
 * seretan yang keluar dari track lalu menuliskan nilai di ujung skala adalah
 * suntingan yang tidak diminta, dan yang menyeretnya tidak melihat apa pun
 * terjadi di bawah kursornya.
 */
export function editAt({
  depths,
  fx,
  fy,
  top,
  base,
  min,
  max,
  logarithmic,
}: {
  depths: number[];
  fx: number;
  fy: number;
  /** Jendela kedalaman track, sebagaimana tergambar. */
  top: number;
  base: number;
  /** Kedua ujung skala mendatar track. */
  min: number;
  max: number;
  logarithmic?: boolean;
}): { md: number; value: number } | null {
  if (!Number.isFinite(fx) || !Number.isFinite(fy)) return null;
  if (fx < 0 || fx > 1 || fy < 0 || fy > 1) return null;
  const span = base - top;
  if (!Number.isFinite(span) || span === 0) return null;

  const md = nearestSample(depths, top + fy * span);
  if (md === null) return null;

  const value = unproject(fx * VIEW_W, min, max, logarithmic);
  if (!Number.isFinite(value)) return null;
  return { md, value };
}

/**
 * Nilai kurva dengan draft yang belum disimpan sudah diterapkan.
 *
 * Satu draft, dua tampilan. Grafik yang membaca nilai mentah sementara tabel
 * membaca draftnya adalah dua jendela yang mengaku menampilkan kurva yang sama
 * sambil menampilkan dua kurva yang berbeda — dan yang mempercayai grafiknya
 * akan menyimpan suntingan yang ia kira tidak jadi.
 *
 * Larik asal dipulangkan apa adanya ketika draftnya kosong, sehingga rujukannya
 * tidak berubah dan yang menggambarnya tidak menghitung ulang tanpa sebab.
 */
export function applyDraft(
  depths: number[],
  values: (number | null)[],
  draft: Map<number, number | null>,
): (number | null)[] {
  if (draft.size === 0) return values;
  return values.map((value, index) => {
    const staged = draft.get(depths[index]);
    return staged !== undefined ? staged : value;
  });
}

/**
 * Kedudukan mendatar sebuah nilai pada track, sebagai pecahan lebar.
 *
 * Pasangan maju dari `editAt`, dan ada di sini karena alasan yang sama: penanda
 * titik yang sudah disunting harus jatuh tepat di atas kurvanya. Menghitungnya
 * sebagai `(nilai − min) / (maks − min)` benar tepat selama sumbunya linear —
 * pada resistivitas yang logaritmik, penandanya melayang jauh dari kurva yang
 * ditandainya, dan yang menyeretnya akan mengira seretannya meleset lalu
 * menyeretnya lagi.
 *
 * `null` untuk nilai yang tidak bisa dipetakan: nol pada sumbu logaritmik,
 * rentang setebal nol, dan bukan-angka.
 */
export function valueFraction(
  value: number,
  min: number,
  max: number,
  logarithmic?: boolean,
): number | null {
  const x = project(value, min, max, logarithmic);
  return Number.isFinite(x) ? x / VIEW_W : null;
}
